import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {KeycloakContext} from "@context/KeycloakContext";


export const NOTIFICATION_TYPES = Object.freeze({
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
})

// How long a toast stays up after its last repeat; a dismissed toast keeps deduping for the same window.
export const TOAST_DELAY = 5000;

// The bell keeps this many notifications, newest first.
export const MAX_NOTIFICATIONS = 50;

export const NotificationsStateContext = createContext(null);
export const NotificationsActionsContext = createContext(null);

// ponytail: localStorage per user until notifications move server-side. Readers tolerate a missing,
// corrupt entry by starting empty; when storage is blocked (null), the history stays in memory.
export const notificationsStorageKey = (userId) => `nco-notifications:${userId}`;
const readStored = (storageKey) => {
    let raw;
    try {
        raw = localStorage.getItem(storageKey);
    } catch {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export default function NotificationsProvider({ children }) {
    const userId = useContext(KeycloakContext)?.user?.id;
    const [toasts, setToasts] = useState([]);
    const [notifications, setNotifications] = useState([]);
    // The bell's dropdown, opened from TopNav or from a toast; focusId is the row to scroll to.
    const [panel, setPanel] = useState({open: false, focusId: null});
    // key -> {toastId, historyId, dismissed, timer} for every toast still queued, on screen, or dismissed
    // but still deduping. A repeat with the same key bumps the toast and its history entry instead of
    // adding more, so one outage (e.g. /query down under a dozen dashboard widgets) shows one toast.
    const active = useRef(new Map());
    // No user yet (or in tests): the history lives in memory only.
    const storageKey = useRef(null);
    const memory = useRef([]);

    // Every history change re-reads storage and writes the result straight back, so tabs sharing the
    // entry apply their changes on top of each other's instead of writing back a stale copy.
    const updateHistory = useCallback((change) => {
        const stored = storageKey.current ? readStored(storageKey.current) : null;
        if (stored === null) storageKey.current = null;
        const next = change(stored ?? memory.current).slice(0, MAX_NOTIFICATIONS);
        memory.current = next;
        if (storageKey.current) {
            try {
                localStorage.setItem(storageKey.current, JSON.stringify(next));
            } catch {
                // Storage full: stop using it, or the next change would re-read the older stored copy.
                storageKey.current = null;
            }
        }
        setNotifications(next);
    }, []);

    useEffect(() => {
        // Notifications raised before sign-in finished have no owner yet; they join this user's history.
        const signingIn = storageKey.current === null;
        storageKey.current = userId ? notificationsStorageKey(userId) : null;
        if (storageKey.current) {
            if (!signingIn) memory.current = [];
            const early = memory.current;
            updateHistory((stored) => [...early.filter((n) => !stored.some((s) => s.id === n.id)), ...stored]);
        }
        // Another tab changed the entry: take its copy as is.
        const onStorage = (event) => {
            if (event.key !== storageKey.current) return;
            memory.current = readStored(storageKey.current);
            setNotifications(memory.current);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [userId, updateHistory]);

    const bump = (current, id, now) =>
        current.map((t) => t.id === id ? {...t, count: t.count + 1, ...(now && {lastAt: now})} : t);
    // A dismissed toast is off screen, so nothing else ends it: it stops deduping TOAST_DELAY after its last repeat.
    const expireLater = (key, entry) => {
        clearTimeout(entry.timer);
        entry.timer = setTimeout(() => active.current.delete(key), TOAST_DELAY);
    };

    const addNotification = useCallback((message, type = NOTIFICATION_TYPES.INFO, key = `${type}:${message}`) => {
            const now = Date.now();
            const entry = active.current.get(key);
            if (entry) {
                setToasts((current) => bump(current, entry.toastId));
                updateHistory((current) => {
                    if (current.some((n) => n.id === entry.historyId)) return bump(current, entry.historyId, now);
                    // Its entry was cleared ("Clear all") or trimmed off the end: start a new one.
                    entry.historyId = crypto.randomUUID();
                    return [{id: entry.historyId, key: key, message: message, type: type, count: 1, read: false,
                        timestamp: now, lastAt: now}, ...current];
                });
                if (entry.dismissed) expireLater(key, entry);
                return;
            }
            const toastId = crypto.randomUUID()
            let historyId = toastId;
            updateHistory((current) => {
                // Another tab may already be counting this outage: join its entry.
                const recent = current.find((n) => n.key === key && now - n.lastAt < TOAST_DELAY);
                if (recent) {
                    historyId = recent.id;
                    return bump(current, recent.id, now);
                }
                return [{id: toastId, key: key, message: message, type: type, count: 1, read: false,
                    timestamp: now, lastAt: now}, ...current];
            });
            active.current.set(key, {toastId: toastId, historyId: historyId, dismissed: false});
            setToasts((current) => [...current, {id: toastId, show: true, message: message, type: type, count: 1}]);
        },  [updateHistory]
    )

    const markRead = useCallback((id) => updateHistory((current) =>
        current.map((n) => n.id === id ? {...n, read: true} : n)), [updateHistory]);
    const markAllRead = useCallback(() => updateHistory((current) =>
        current.map((n) => ({...n, read: true}))), [updateHistory]);
    // Empties the bell and removes the stored entry (the dropdown's "Clear all" and sign-out).
    const clearNotifications = useCallback(() => {
        memory.current = [];
        try {
            if (storageKey.current) localStorage.removeItem(storageKey.current);
        } catch {
            // Blocked storage: nothing was stored.
        }
        setNotifications([]);
    }, []);

    const hideToast = useCallback((id) => setToasts((current) =>
      current.map((t) => t.id === id ? {...t, show: false} : t)), []);
    // The user closed it: mark it read and hide it, but keep counting repeats in the background until they stop.
    const dismissToast = useCallback((id) => {
        for (const [key, entry] of active.current) {
            if (entry.toastId === id) {
                entry.dismissed = true;
                expireLater(key, entry);
                markRead(entry.historyId);
            }
        }
        hideToast(id);
    }, [hideToast, markRead]);
    const removeToast = useCallback((id) => {
        for (const [key, entry] of active.current) {
            if (entry.toastId === id && !entry.dismissed) active.current.delete(key);
        }
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);

    const setPanelOpen = useCallback((open) => setPanel({open: open, focusId: null}), []);
    // Clicking a toast: close it and show its full message in the bell's dropdown.
    const openToastInPanel = useCallback((toastId) => {
        const entry = [...active.current.values()].find((e) => e.toastId === toastId);
        dismissToast(toastId);
        setPanel({open: true, focusId: entry?.historyId ?? null});
    }, [dismissToast]);

    const actions = useMemo(() => ({addNotification, hideToast, dismissToast, removeToast, markRead, markAllRead,
            clearNotifications, setPanelOpen, openToastInPanel}),
        [addNotification, hideToast, dismissToast, removeToast, markRead, markAllRead, clearNotifications,
            setPanelOpen, openToastInPanel]);
    const states = useMemo(() => ({notifications, toasts, panel}),
        [notifications, toasts, panel])
    return (
        <NotificationsActionsContext.Provider value={actions}>
            <NotificationsStateContext.Provider value={states}>
                {children}
            </NotificationsStateContext.Provider>
        </NotificationsActionsContext.Provider>
    );
}

export const useNotificationsState = () => useContext(NotificationsStateContext);
export const useNotifications = () => useContext(NotificationsActionsContext);
